import React, { useMemo, useState } from "react";
import {
  Alert,
  Box,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Typography,
  Stack,
  TextField,
  InputAdornment,
  Button,
  Paper,
  Chip,
  Avatar,
  FormControl,
  LinearProgress,
  Select,
  MenuItem,
} from "@mui/material";
import {
  Search,
  Download,
  TaskAlt,
  Visibility,
  AccessTime,
  TrendingUp,
  DescriptionOutlined,
  Close,
} from "@mui/icons-material";
import SarIcon from "../../icons/SarIcon";
import AccountBalanceOutlinedIcon from "@mui/icons-material/AccountBalanceOutlined";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import { useNavigate } from "react-router-dom";
import {
  useGetAdminLoansQuery,
  useReviewAdminLoanMutation,
  useSignOffAdminLoanMutation,
} from "../../../redux/api/authApi";
import AdminLoansSkeleton from "./AdminLoansSkeleton";
import AdminLoanSignOffModal from "./AdminLoanSignOffModal";
import AdminLoanDetailsModal from "./AdminLoanDetailsModal";
import {
  formatApplied,
  formatPercent,
  formatSar,
  normalizeStatus,
  statusConfig,
  statusLabel,
} from "./loanFormatters";

const PAGE_SIZE = 20;

const cardSx = {
  borderRadius: 1.5,
  boxShadow: (theme) => theme.palette.shadow.statCard,
  border: (theme) => `1px solid ${theme.palette.border.light}`,
};

function LoanStatusChip({ status }) {
  const cfg = statusConfig(status);
  const key = cfg?.chipKey || "neutral";
  const normalized = normalizeStatus(status);
  const Icon =
    normalized === "PENDING_REVIEW"
      ? AccessTime
      : normalized === "ACTIVE"
        ? TrendingUp
        : normalized === "APPROVED"
          ? TaskAlt
          : null;
  return (
    <Chip
      icon={Icon ? <Icon sx={{ fontSize: 14 }} /> : undefined}
      label={statusLabel(status)}
      size="small"
      sx={{
        fontSize: 11,
        fontWeight: 600,
        height: 24,
        px: 0.5,
        borderRadius: 999,
        bgcolor: (theme) => theme.palette.chips[key].bg,
        color: (theme) => theme.palette.chips[key].text,
        "& .MuiChip-icon": {
          color: (theme) => theme.palette.chips[key].text,
        },
      }}
    />
  );
}

/** `key` is the `tab` query param the API expects. */
const TABS = [
  { key: "all", label: "All Loans" },
  { key: "pending", label: "Pending" },
  { key: "signoff", label: "Awaiting Sign-Off", icon: <DescriptionOutlined sx={{ fontSize: 16 }} /> },
  { key: "active", label: "Active" },
  { key: "repaid", label: "Repaid" },
];

/** Stacked label + field used for the filters card */
const StackedFilter = ({ label, children }) => (
  <Box sx={{ flex: 1, minWidth: 0 }}>
    <Typography
      fontSize={13}
      fontWeight={700}
      sx={{ color: "text.primary", mb: 1 }}
    >
      {label}
    </Typography>
    {children}
  </Box>
);

const KpiCard = ({ title, value, hint, hintColor, icon, iconBg, iconColor }) => (
  <Paper elevation={0} sx={{ ...cardSx, p: 2.5, flex: 1 }}>
    <Box
      sx={{
        width: 40,
        height: 40,
        borderRadius: 1,
        bgcolor: iconBg,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        mb: 2,
      }}
    >
      {React.cloneElement(icon, { sx: { color: iconColor, fontSize: 22 } })}
    </Box>
    <Typography variant="body2" sx={{ color: "text.muted", fontWeight: 500 }}>
      {title}
    </Typography>
    <Typography
      variant="h5"
      fontWeight={500}
      sx={{ color: "text.primary", mt: 0.5 }}
    >
      {value}
    </Typography>
    <Typography
      variant="caption"
      sx={{ color: hintColor, display: "block", mt: 0.5, fontWeight: 400 }}
    >
      {hint}
    </Typography>
  </Paper>
);

/**
 * Loan metrics as equal-width columns filling the row, so the same stat lines up
 * across every tab's card.
 */
const MetricRow = ({ items }) => (
  <Box
    sx={{
      width: "100%",
      display: "grid",
      gridTemplateColumns: {
        xs: "1fr 1fr",
        sm: `repeat(${items.length}, minmax(0, 1fr))`,
      },
      gap: 2,
    }}
  >
    {items.map((cell) => (
      <Box key={cell.label} sx={{ minWidth: 0 }}>
        <Typography variant="caption" sx={{ color: "text.muted" }}>
          {cell.label}
        </Typography>
        <Typography variant="body2" fontWeight={700} sx={{ color: "text.primary" }}>
          {cell.value}
        </Typography>
      </Box>
    ))}
  </Box>
);

/**
 * Card for a disbursed loan — repayment progress from `activeDetails` in place
 * of the application metrics shown on pending cards.
 */
const ActiveLoanCard = ({ loan, onViewDetails }) => {
  const details = loan.activeDetails || {};
  const progress = details.paymentProgress || {};
  const currency = details.currency || loan.currency;

  const percent = Number(progress.percentComplete);
  const safePercent = Number.isFinite(percent) ? Math.min(100, Math.max(0, percent)) : 0;
  const totalPaid = progress.totalPaid ?? loan.totalRepaid;
  const originalLoan = details.originalLoan ?? loan.amount;

  const stats = [
    {
      label: "Interest Rate",
      value: formatPercent(details.interestRate ?? loan.interestRate),
    },
    {
      label: "Term",
      value: Number.isFinite(Number(details.termMonths ?? loan.termMonths))
        ? `${details.termMonths ?? loan.termMonths} months`
        : "—",
    },
    { label: "LTV Ratio", value: formatPercent(loan.ltvRatio) },
  ];

  return (
    <Paper elevation={0} sx={{ ...cardSx, p: 3 }}>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={2}
        justifyContent="space-between"
        alignItems={{ xs: "flex-start", sm: "flex-start" }}
      >
        <Stack direction="row" spacing={2} alignItems="flex-start" sx={{ minWidth: 0 }}>
          <Avatar
            src={loan.user?.profileImage || undefined}
            alt={loan.user?.name || ""}
            sx={{
              width: 56,
              height: 56,
              bgcolor: (theme) => theme.palette.statIcon.users.bg,
              color: (theme) => theme.palette.statIcon.users.color,
              fontWeight: 700,
            }}
          >
            {(loan.user?.name || "?").charAt(0).toUpperCase()}
          </Avatar>
          <Box sx={{ minWidth: 0 }}>
            <Stack direction="row" alignItems="center" spacing={1.5} mb={0.5}>
              <Typography
                variant="subtitle2"
                fontWeight={700}
                sx={{ color: "text.primary" }}
              >
                {loan.loanId}
              </Typography>
              <LoanStatusChip status={loan.status} />
            </Stack>
            <Typography fontWeight={700} fontSize={15} sx={{ color: "text.primary" }}>
              {loan.user?.name || "—"}
            </Typography>
            <Typography variant="body2" sx={{ color: "text.muted" }}>
              {loan.assetName || "—"}
            </Typography>
          </Box>
        </Stack>
        <Button
          variant="outlined"
          onClick={() => onViewDetails?.(loan)}
          startIcon={<Visibility sx={{ fontSize: 16 }} />}
          sx={{
            textTransform: "none",
            fontWeight: 600,
            borderRadius: 1.25,
            borderColor: "border.light",
            color: "text.primary",
            px: 2,
            flexShrink: 0,
          }}
        >
          View Details
        </Button>
      </Stack>

      {/* Repayment progress */}
      <Box sx={{ mt: 2.5 }}>
        <Stack direction="row" justifyContent="space-between" spacing={2}>
          <Typography variant="body2" sx={{ color: "text.muted" }}>
            Repayment Progress
          </Typography>
          <Typography variant="body2" fontWeight={700} sx={{ color: "text.primary" }}>
            {`${formatSar(totalPaid, currency)} / ${formatSar(originalLoan, currency)}`}
          </Typography>
        </Stack>
        <LinearProgress
          variant="determinate"
          value={safePercent}
          sx={{
            mt: 1,
            height: 8,
            borderRadius: 999,
            bgcolor: (theme) => theme.palette.accent.blue.borderFaint,
            "& .MuiLinearProgress-bar": {
              borderRadius: 999,
              bgcolor: (theme) => theme.palette.accent.blue.solid,
            },
          }}
        />
        <Stack direction="row" justifyContent="space-between" spacing={2} sx={{ mt: 1 }}>
          <Typography variant="caption" sx={{ color: "text.muted" }}>
            {`${safePercent.toLocaleString("en-US", {
              maximumFractionDigits: 1,
            })}% repaid`}
          </Typography>
          <Typography variant="caption" sx={{ color: "text.muted" }}>
            {`Monthly: ${formatSar(
              details.monthlyPayment ?? loan.monthlyPayment,
              currency,
            )}`}
          </Typography>
        </Stack>
      </Box>

      <Divider sx={{ my: 2.5 }} />

      <MetricRow items={stats} />
    </Paper>
  );
};

const TabPill = ({ active, label, icon, onClick }) => (
  <Button
    size="small"
    onClick={onClick}
    startIcon={icon}
    sx={{
      textTransform: "none",
      fontSize: 13,
      fontWeight: 600,
      px: 2.5,
      py: 0.75,
      borderRadius: 999,
      minWidth: 0,
      bgcolor: active ? "background.paper" : "transparent",
      color: active ? "text.primary" : "text.muted",
      boxShadow: active ? "0 1px 2px rgba(15,23,42,0.08)" : "none",
      "&:hover": {
        bgcolor: active ? "background.paper" : "transparent",
      },
    }}
  >
    {label}
  </Button>
);

/**
 * Approve / reject a loan application. Rejecting requires a reason, which is
 * the only field sent alongside `action`.
 */
function LoanReviewModal({ open, loan, onClose, onSubmit, submitting, error }) {
  const [action, setAction] = useState("approve");
  const [reason, setReason] = useState("");
  const [touched, setTouched] = useState(false);

  // Reset whenever a different loan is opened.
  React.useEffect(() => {
    if (!open) return;
    setAction("approve");
    setReason("");
    setTouched(false);
  }, [open, loan?._id]);

  const reasonMissing = action === "reject" && !reason.trim();

  const handleSubmit = () => {
    setTouched(true);
    if (reasonMissing) return;
    onSubmit({ action, rejectionReason: reason.trim() });
  };

  return (
    <Dialog open={open} onClose={submitting ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle
        sx={{ fontSize: 18, fontWeight: 700, color: "text.primary", pr: 6 }}
      >
        Review Loan Application
        <Button
          onClick={onClose}
          disabled={submitting}
          aria-label="Close"
          sx={{
            position: "absolute",
            top: 12,
            right: 12,
            minWidth: 0,
            p: 1,
            color: "text.muted",
          }}
        >
          <Close sx={{ fontSize: 18 }} />
        </Button>
      </DialogTitle>
      <DialogContent dividers>
        {loan && (
          <Stack spacing={0.5} sx={{ mb: 2 }}>
            <Typography sx={{ fontSize: 15, fontWeight: 700, color: "text.primary" }}>
              {loan.loanId}
            </Typography>
            <Typography variant="body2" sx={{ color: "text.muted" }}>
              {`${loan.user?.name || "—"} · ${loan.assetName || "—"}`}
            </Typography>
            <Typography variant="body2" sx={{ color: "text.muted" }}>
              {`${formatSar(loan.amount, loan.currency)} · ${formatPercent(
                loan.interestRate,
              )} · ${loan.termMonths} months · LTV ${formatPercent(loan.ltvRatio)}`}
            </Typography>
          </Stack>
        )}

        <Divider sx={{ mb: 2 }} />

        <Typography fontSize={13} fontWeight={700} sx={{ color: "text.primary", mb: 1 }}>
          Decision
        </Typography>
        <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
          {[
            { value: "approve", label: "Approve" },
            { value: "reject", label: "Reject" },
          ].map((opt) => {
            const selected = action === opt.value;
            const isReject = opt.value === "reject";
            return (
              <Button
                key={opt.value}
                onClick={() => setAction(opt.value)}
                variant={selected ? "contained" : "outlined"}
                disableElevation
                sx={{
                  flex: 1,
                  textTransform: "none",
                  fontWeight: 700,
                  borderRadius: 1.25,
                  ...(selected
                    ? {
                        bgcolor: (theme) =>
                          isReject
                            ? theme.palette.chips.error.text
                            : theme.palette.success.main,
                        color: "common.white",
                        "&:hover": {
                          bgcolor: (theme) =>
                            isReject
                              ? theme.palette.chips.error.text
                              : theme.palette.success.main,
                        },
                      }
                    : { borderColor: "border.light", color: "text.primary" }),
                }}
              >
                {opt.label}
              </Button>
            );
          })}
        </Stack>

        {action === "reject" && (
          <>
            <Typography
              fontSize={13}
              fontWeight={700}
              sx={{ color: "text.primary", mb: 1 }}
            >
              Rejection Reason *
            </Typography>
            <TextField
              fullWidth
              multiline
              minRows={3}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              onBlur={() => setTouched(true)}
              placeholder="Explain why this application is being rejected…"
              error={touched && reasonMissing}
              helperText={
                touched && reasonMissing
                  ? "A rejection reason is required."
                  : "Shared with the applicant."
              }
              sx={{ "& .MuiOutlinedInput-root": { borderRadius: 1.25 } }}
            />
          </>
        )}

        {error ? (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        ) : null}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button
          onClick={onClose}
          disabled={submitting}
          sx={{ textTransform: "none", fontWeight: 600, color: "text.primary" }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={submitting || reasonMissing}
          startIcon={
            submitting ? (
              <CircularProgress size={14} sx={{ color: "common.white" }} />
            ) : null
          }
          sx={{
            textTransform: "none",
            fontWeight: 700,
            borderRadius: 1.25,
            px: 2.5,
            bgcolor: (theme) =>
              action === "reject"
                ? theme.palette.chips.error.text
                : theme.palette.success.main,
            "&:hover": {
              bgcolor: (theme) =>
                action === "reject"
                  ? theme.palette.chips.error.text
                  : theme.palette.success.main,
            },
          }}
        >
          {action === "reject" ? "Reject Loan" : "Approve Loan"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default function AdminLoans() {
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("All Statuses");
  const [tab, setTab] = useState("all");

  const [reviewLoan, setReviewLoan] = useState(null);
  const [reviewError, setReviewError] = useState("");
  const [signOffLoan, setSignOffLoan] = useState(null);
  const [signOffError, setSignOffError] = useState("");
  const [detailsLoan, setDetailsLoan] = useState(null);

  const { data, isLoading, isFetching, isError, error, refetch } =
    useGetAdminLoansQuery({ page: 1, limit: PAGE_SIZE, tab });
  const [submitReview, { isLoading: reviewSubmitting }] =
    useReviewAdminLoanMutation();
  const [submitSignOff, { isLoading: signOffSubmitting }] =
    useSignOffAdminLoanMutation();

  const openSignOff = (loan) => {
    setSignOffError("");
    setSignOffLoan(loan);
  };

  const closeSignOff = () => {
    setSignOffError("");
    setSignOffLoan(null);
  };

  const handleSignOffSubmit = async ({ signatureData, notes }) => {
    setSignOffError("");
    try {
      await submitSignOff({
        loanId: signOffLoan?._id,
        signatureData,
        notes,
      }).unwrap();
      closeSignOff();
    } catch (err) {
      setSignOffError(
        err?.data?.message || "Could not complete the sign-off. Please try again.",
      );
    }
  };

  const openReview = (loan) => {
    setReviewError("");
    setReviewLoan(loan);
  };

  const closeReview = () => {
    setReviewError("");
    setReviewLoan(null);
  };

  const handleReviewSubmit = async ({ action, rejectionReason }) => {
    setReviewError("");
    try {
      await submitReview({
        loanId: reviewLoan?._id,
        action,
        rejectionReason,
      }).unwrap();
      closeReview();
    } catch (err) {
      setReviewError(
        err?.data?.message ||
          `Could not ${action} this loan. Please try again.`,
      );
    }
  };

  const payload = data?.data;
  const loans = useMemo(
    () => (Array.isArray(payload?.items) ? payload.items : []),
    [payload],
  );
  const counts = payload?.counts || {};

  /** Status values present in the loaded page, for the Status filter options. */
  const statusOptions = useMemo(() => {
    const labels = loans.map((l) => statusLabel(l.status));
    return [...new Set(labels)].sort();
  }, [loans]);

  /**
   * Tabs are server-side; these controls narrow the page that came back, since
   * the endpoint takes no search/status params.
   */
  const visibleLoans = useMemo(() => {
    const q = search.trim().toLowerCase();
    return loans.filter((l) => {
      const matchesSearch =
        !q ||
        String(l.user?.name || "").toLowerCase().includes(q) ||
        String(l.loanId || "").toLowerCase().includes(q) ||
        String(l.assetName || "").toLowerCase().includes(q);
      const matchesStatus =
        statusFilter === "All Statuses" || statusLabel(l.status) === statusFilter;
      return matchesSearch && matchesStatus;
    });
  }, [loans, search, statusFilter]);

  const kpis = [
    {
      title: "Total Loans",
      value: String(Number(counts.all) || 0),
      hint: "All applications",
      hintColor: (theme) => theme.palette.statIcon.revenue.color,
      icon: <DescriptionOutlined />,
      iconBg: (theme) => theme.palette.statIcon.revenue.bg,
      iconColor: (theme) => theme.palette.statIcon.revenue.color,
    },
    {
      title: "Pending Review",
      value: String(Number(counts.pending) || 0),
      hint: "Awaiting approval",
      hintColor: (theme) => theme.palette.chips.pending.text,
      icon: <AccessTime />,
      iconBg: (theme) => theme.palette.chips.pending.bg,
      iconColor: (theme) => theme.palette.chips.pending.text,
    },
    {
      // Not returned by /api/admin/loans — needs a stats endpoint.
      title: "Total Disbursed",
      value: "—",
      hint: `${Number(counts.active) || 0} active loan${
        Number(counts.active) === 1 ? "" : "s"
      }`,
      hintColor: (theme) => theme.palette.chips.success.text,
      icon: <SarIcon />,
      iconBg: (theme) => theme.palette.statIcon.users.bg,
      iconColor: (theme) => theme.palette.statIcon.users.color,
    },
    {
      // Not returned by /api/admin/loans — needs a stats endpoint.
      title: "Default Rate",
      value: "—",
      hint: "Industry avg: 2.5%",
      hintColor: (theme) => theme.palette.statIcon.assets.color,
      icon: <TrendingUp />,
      iconBg: (theme) => theme.palette.statIcon.assets.bg,
      iconColor: (theme) => theme.palette.statIcon.assets.color,
    },
  ];

  if (isLoading) {
    return <AdminLoansSkeleton />;
  }

  return (
    <Box>
      {/* Back link */}
      <Button
        onClick={() => navigate("/admin")}
        startIcon={<ArrowBackIosNewIcon sx={{ fontSize: 14 }} />}
        sx={{
          textTransform: "none",
          color: "text.primary",
          mb: 2,
          px: 0,
          "&:hover": { backgroundColor: "transparent" },
        }}
      >
        Back to Dashboard
      </Button>

      {/* Page header */}
      <Stack
        direction={{ xs: "column", sm: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "flex-start", sm: "center" }}
        spacing={2}
        sx={{ mb: 3 }}
      >
        <Stack direction="row" spacing={2} alignItems="center">
          <Box
            sx={{
              width: 56,
              height: 56,
              borderRadius: 1.5,
              bgcolor: "background.headerIcon",
              border: (theme) => `1px solid ${theme.palette.border.light}`,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <AccountBalanceOutlinedIcon sx={{ color: "text.slate", fontSize: 28 }} />
          </Box>
          <Box>
            <Typography
              variant="h5"
              fontWeight={700}
              sx={{ color: "text.primary", letterSpacing: -0.5 }}
            >
              Loan Management
            </Typography>
            <Typography variant="body2" sx={{ color: "text.muted", mt: 0.5 }}>
              Monitor and manage mortgage loan applications
            </Typography>
          </Box>
        </Stack>
        <Button
          variant="outlined"
          startIcon={<Download />}
          sx={{
            textTransform: "none",
            fontWeight: 600,
            borderRadius: 1.25,
            borderColor: "border.light",
            color: "text.primary",
            backgroundColor: "background.paper",
            px: 2,
            py: 1,
            alignSelf: { xs: "stretch", sm: "auto" },
          }}
        >
          Export Report
        </Button>
      </Stack>

      {isError ? (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => refetch()}>
          {error?.data?.message || error?.error || "Failed to load loans."}
        </Alert>
      ) : null}

      {/* KPI cards */}
      <Stack
        direction={{ xs: "column", md: "row" }}
        spacing={3}
        sx={{ mb: 3 }}
      >
        {kpis.map((k) => (
          <KpiCard key={k.title} {...k} />
        ))}
      </Stack>

      {/* Filters card */}
      <Paper elevation={0} sx={{ ...cardSx, p: 3, mb: 3 }}>
        <Stack
          direction={{ xs: "column", md: "row" }}
          spacing={3}
          alignItems="flex-start"
        >
          <StackedFilter label="Search">
            <TextField
              size="small"
              fullWidth
              placeholder="Search by name, ID, or property..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search fontSize="small" sx={{ color: "text.muted" }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: 1.25,
                },
              }}
            />
          </StackedFilter>
          <StackedFilter label="Status">
            <FormControl size="small" fullWidth>
              <Select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                sx={{ borderRadius: 1.25 }}
              >
                <MenuItem value="All Statuses">All Statuses</MenuItem>
                {statusOptions.map((label) => (
                  <MenuItem key={label} value={label}>
                    {label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </StackedFilter>
        </Stack>
      </Paper>

      {/* Tab pill group */}
      <Box sx={{ mb: 3 }}>
        <Stack
          direction="row"
          flexWrap="wrap"
          sx={{
            display: "inline-flex",
            p: 0.5,
            borderRadius: 999,
            bgcolor: "background.subtle",
            border: (theme) => `1px solid ${theme.palette.border.light}`,
          }}
        >
          {TABS.map((t) => (
            <TabPill
              key={t.key}
              active={tab === t.key}
              label={`${t.label} (${Number(counts[t.key]) || 0})`}
              icon={t.icon}
              onClick={() => setTab(t.key)}
            />
          ))}
        </Stack>
      </Box>

      {/* Loan cards */}
      <Stack spacing={2}>
        {visibleLoans.length === 0 ? (
          <Paper elevation={0} sx={{ ...cardSx, p: 4, textAlign: "center" }}>
            <Typography sx={{ color: "text.muted" }}>
              {isFetching
                ? "Loading loans…"
                : loans.length === 0
                  ? "No loans in this category."
                  : "No loans match your filters."}
            </Typography>
          </Paper>
        ) : (
          visibleLoans.map((loan) =>
            loan.activeDetails ? (
              <ActiveLoanCard
                key={loan._id || loan.loanId}
                loan={loan}
                onViewDetails={setDetailsLoan}
              />
            ) : (
            <Paper key={loan._id || loan.loanId} elevation={0} sx={{ ...cardSx, p: 3 }}>
              <Stack
                direction={{ xs: "column", lg: "row" }}
                spacing={3}
                justifyContent="space-between"
                alignItems={{ xs: "flex-start", lg: "center" }}
              >
                <Stack
                  direction="row"
                  spacing={2}
                  alignItems="flex-start"
                  sx={{ flex: 1, width: "100%" }}
                >
                  <Avatar
                    src={loan.user?.profileImage || undefined}
                    alt={loan.user?.name || ""}
                    sx={{
                      width: 56,
                      height: 56,
                      bgcolor: (theme) => theme.palette.statIcon.users.bg,
                      color: (theme) => theme.palette.statIcon.users.color,
                      fontWeight: 700,
                    }}
                  >
                    {(loan.user?.name || "?").charAt(0).toUpperCase()}
                  </Avatar>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Stack
                      direction="row"
                      alignItems="center"
                      spacing={1.5}
                      mb={0.5}
                    >
                      <Typography
                        variant="subtitle2"
                        fontWeight={700}
                        sx={{ color: "text.primary" }}
                      >
                        {loan.loanId}
                      </Typography>
                      <LoanStatusChip status={loan.status} />
                    </Stack>
                    <Typography
                      fontWeight={700}
                      fontSize={15}
                      sx={{ color: "text.primary" }}
                    >
                      {loan.user?.name || "—"}
                    </Typography>
                    <Typography variant="body2" sx={{ color: "text.muted", mb: 2 }}>
                      {loan.assetName || "—"}
                    </Typography>

                    <MetricRow
                      items={[
                        {
                          label: "Loan Amount",
                          value: formatSar(loan.amount, loan.currency),
                        },
                        {
                          label: "Interest Rate",
                          value: formatPercent(loan.interestRate),
                        },
                        { label: "LTV Ratio", value: formatPercent(loan.ltvRatio) },
                      ]}
                    />
                  </Box>
                </Stack>
                <Stack
                  direction={{ xs: "row", lg: "column" }}
                  spacing={1}
                  sx={{
                    alignSelf: { lg: "flex-start" },
                    width: { xs: "100%", lg: "auto" },
                    flexShrink: 0,
                  }}
                >
                  <Button
                    variant="outlined"
                    onClick={() => setDetailsLoan(loan)}
                    startIcon={<Visibility sx={{ fontSize: 16 }} />}
                    sx={{
                      textTransform: "none",
                      fontWeight: 600,
                      borderRadius: 1.25,
                      borderColor: "border.light",
                      color: "text.primary",
                      px: 2,
                      flex: { xs: 1, lg: "none" },
                    }}
                  >
                    View Details
                  </Button>
                  {statusConfig(loan.status)?.reviewable && (
                    <Button
                      variant="contained"
                      onClick={() => openReview(loan)}
                      startIcon={<TaskAlt sx={{ fontSize: 16 }} />}
                      sx={{
                        textTransform: "none",
                        fontWeight: 700,
                        borderRadius: 1.25,
                        px: 2,
                        flex: { xs: 1, lg: "none" },
                        background: (theme) => theme.palette.gradients.teal,
                        "&:hover": {
                          background: (theme) => theme.palette.gradients.tealHover,
                        },
                      }}
                    >
                      Review
                    </Button>
                  )}
                  {statusConfig(loan.status)?.signable && (
                    <Button
                      variant="contained"
                      onClick={() => openSignOff(loan)}
                      startIcon={<DescriptionOutlined sx={{ fontSize: 16 }} />}
                      sx={{
                        textTransform: "none",
                        fontWeight: 700,
                        borderRadius: 1.25,
                        px: 2,
                        flex: { xs: 1, lg: "none" },
                        whiteSpace: "nowrap",
                        color: "common.white",
                        bgcolor: (theme) => theme.palette.accent.blue.solid,
                        boxShadow: "none",
                        "&:hover": {
                          bgcolor: (theme) => theme.palette.accent.blue.actionHover,
                          boxShadow: "none",
                        },
                      }}
                    >
                      Sign Off
                    </Button>
                  )}
                </Stack>
              </Stack>
            </Paper>
            ),
          )
        )}
      </Stack>

      <LoanReviewModal
        open={Boolean(reviewLoan)}
        loan={reviewLoan}
        onClose={closeReview}
        onSubmit={handleReviewSubmit}
        submitting={reviewSubmitting}
        error={reviewError}
      />

      <AdminLoanSignOffModal
        open={Boolean(signOffLoan)}
        loan={signOffLoan}
        onClose={closeSignOff}
        onSubmit={handleSignOffSubmit}
        submitting={signOffSubmitting}
        error={signOffError}
      />

      <AdminLoanDetailsModal
        open={Boolean(detailsLoan)}
        loan={detailsLoan}
        onClose={() => setDetailsLoan(null)}
      />
    </Box>
  );
}
